import React, { useState, useEffect, useContext } from "react";
import {
  Search,
  Filter,
  Calendar,
  Clock,
  User,
  FileText,
  Eye,
  Edit,
  Trash2,
  Plus,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

// Import your API function
import {
  updateAppointment,
  deleteAppointment,
  getAppointmentsByStatus,
  getAppointmentsByType,
  getAppointmentsByDoctor,
  getAppointmentsByDate,
  getAppointmentsByDateRange,
  getTodaysAppointments,
  confirmAppointment,
  cancelAppointment,
  completeAppointment,
} from "../../../services/appointmentApi";

// Import AuthContext
import { AuthContext } from "../../../context/AuthContext";

const AppointmentManager = () => {
  // Get context functions and state
  const {
    getAppointments,
    allAppointments,
    allAppointmentsLoading,
    allAppointmentsError,
  } = useContext(AuthContext);

  // Local state for filtered appointments and UI
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [displayedAppointments, setDisplayedAppointments] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    type: "",
    doctor: "",
    date: "",
    startDate: "",
    endDate: "",
    quickFilter: "", // New quick filter for Today, This Week, etc.
  });

  // Enhanced Pagination state with better defaults
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Default to 10 items per page
  const [totalPages, setTotalPages] = useState(1);

  const [showFilters, setShowFilters] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Static data for dropdowns
  const appointmentTypes = [
    "checkup",
    "vaccination",
    "surgery",
    "emergency",
    "grooming",
    "dental",
  ];
  const doctors = [
    "Dr. Smith",
    "Dr. Johnson",
    "Dr. Brown",
    "Dr. Davis",
    "Dr. Wilson",
  ];
  const statuses = ["pending", "confirmed", "cancelled", "completed"];

  // Enhanced items per page options
  const itemsPerPageOptions = [10, 20, 50, 100];

  // Quick filter options
  const quickFilters = [
    { value: "", label: "All Time" },
    { value: "today", label: "Today" },
    { value: "tomorrow", label: "Tomorrow" },
    { value: "this_week", label: "This Week" },
    { value: "next_week", label: "Next Week" },
    { value: "this_month", label: "This Month" },
  ];

  // Helper function to safely get doctor name
  const getDoctorName = (doctor) => {
    if (!doctor) return "Not assigned";
    if (typeof doctor === "string") return doctor;
    if (typeof doctor === "object")
      return doctor.name || doctor.email || "Unknown Doctor";
    return "Not assigned";
  };

  // Helper function to safely get pet name - Updated to handle ObjectId case
  const getPetName = (pets) => {
    if (!pets) return "Unknown Pet";

    // If pets is just an ObjectId string, return a placeholder
    if (typeof pets === "string") {
      return `Pet (ID: ${pets.substring(0, 8)}...)`;
    }

    return pets.name || "Un Named Pet";
  };

  // Helper function to safely get owner name - Updated to handle ObjectId case
  const getOwnerName = (pets) => {
    if (!pets) return "Unknown Owner";

    // If pets is just an ObjectId string, we can't get owner info
    if (typeof pets === "string") {
      return "Owner (Pet not populated)";
    }

    // If pets is an object but not populated
    if (typeof pets === "object" && pets._id && !pets.ownerId) {
      return "Owner (Pet not populated)";
    }

    // If pets is an array, check first pet
    if (Array.isArray(pets) && pets.length > 0) {
      return getOwnerName(pets[0]);
    }

    // Handle properly populated pet object
    if (typeof pets === "object" && pets.ownerId) {
      const ownerPaths = [
        // Direct paths that match your backend population structure
        pets?.ownerId?.userDetails?.name,
        pets?.ownerId?.userDetails?.firstName &&
        pets?.ownerId?.userDetails?.lastName
          ? `${pets.ownerId.userDetails.firstName} ${pets.ownerId.userDetails.lastName}`
          : null,
        // Alternative paths
        pets?.owner?.userDetails?.name,
        pets?.owner?.userDetails?.firstName &&
        pets?.owner?.userDetails?.lastName
          ? `${pets.owner.userDetails.firstName} ${pets.owner.userDetails.lastName}`
          : null,
        pets?.owner?.name,
        pets?.ownerId?.name,
        pets?.ownerId?.email, // Fallback to email if name not available
      ];

      for (const path of ownerPaths) {
        if (path && typeof path === "string" && path.trim() !== "") {
          return path.trim();
        }
      }

      // If ownerId exists but is not populated
      if (pets?.ownerId?._id || pets?.ownerId) {
        return "Owner (Not populated)";
      }
    }

    return "Unknown Owner";
  };

  // Helper function to get date ranges for quick filters
  const getDateRangeForQuickFilter = (filterValue) => {
    const today = new Date();
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const endOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59
    );

    switch (filterValue) {
      case "today":
        return {
          startDate: startOfToday.toISOString().split("T")[0],
          endDate: startOfToday.toISOString().split("T")[0],
        };

      case "tomorrow":
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        return {
          startDate: tomorrow.toISOString().split("T")[0],
          endDate: tomorrow.toISOString().split("T")[0],
        };

      case "this_week":
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return {
          startDate: startOfWeek.toISOString().split("T")[0],
          endDate: endOfWeek.toISOString().split("T")[0],
        };

      case "next_week":
        const nextWeekStart = new Date(today);
        nextWeekStart.setDate(today.getDate() - today.getDay() + 7);
        const nextWeekEnd = new Date(nextWeekStart);
        nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
        return {
          startDate: nextWeekStart.toISOString().split("T")[0],
          endDate: nextWeekEnd.toISOString().split("T")[0],
        };

      case "this_month":
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0
        );
        return {
          startDate: startOfMonth.toISOString().split("T")[0],
          endDate: endOfMonth.toISOString().split("T")[0],
        };

      default:
        return null;
    }
  };

  // Fetch appointments on component mount
  useEffect(() => {
    getAppointments();
  }, []);

  // Update filtered appointments when context appointments change
  useEffect(() => {
    if (allAppointments?.length >= 0) {
      // Debug: Log the first appointment to see the actual structure
      if (allAppointments && allAppointments.length > 0) {
        console.log("Sample appointment structure:", allAppointments[0]);
        console.log("Sample pets structure:", allAppointments[0]?.pets);
        console.log("Sample doctor structure:", allAppointments[0]?.doctor);
      }

      setFilteredAppointments(
        Array.isArray(allAppointments) ? allAppointments : []
      );
    }
  }, [allAppointments]);

  // Enhanced pagination effect - Apply pagination whenever filtered appointments or pagination settings change
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredAppointments.slice(startIndex, endIndex);

    setDisplayedAppointments(paginatedData);
    setTotalPages(Math.ceil(filteredAppointments.length / itemsPerPage));
  }, [filteredAppointments, currentPage, itemsPerPage]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, itemsPerPage]);

  // Advanced filtering with API calls when possible
  const applyFilters = async () => {
    try {
      let result;
      let shouldUseAPI = false;

      // Handle quick filters first
      if (filters.quickFilter) {
        const dateRange = getDateRangeForQuickFilter(filters.quickFilter);
        if (dateRange) {
          if (filters.quickFilter === "today") {
            result = await getTodaysAppointments();
            shouldUseAPI = true;
          } else {
            result = await getAppointmentsByDateRange(
              dateRange.startDate,
              dateRange.endDate
            );
            shouldUseAPI = true;
          }
        }
      }
      // Use specific API endpoints when filtering by single criteria
      else if (
        filters.status &&
        !filters.type &&
        !filters.doctor &&
        !filters.date &&
        !filters.search
      ) {
        result = await getAppointmentsByStatus(filters.status);
        shouldUseAPI = true;
      } else if (
        filters.type &&
        !filters.status &&
        !filters.doctor &&
        !filters.date &&
        !filters.search
      ) {
        result = await getAppointmentsByType(filters.type);
        shouldUseAPI = true;
      } else if (
        filters.doctor &&
        !filters.status &&
        !filters.type &&
        !filters.date &&
        !filters.search
      ) {
        result = await getAppointmentsByDoctor(filters.doctor);
        shouldUseAPI = true;
      } else if (
        filters.date &&
        !filters.status &&
        !filters.type &&
        !filters.doctor &&
        !filters.search
      ) {
        result = await getAppointmentsByDate(filters.date);
        shouldUseAPI = true;
      } else if (
        filters.startDate &&
        filters.endDate &&
        !filters.status &&
        !filters.type &&
        !filters.doctor &&
        !filters.search
      ) {
        result = await getAppointmentsByDateRange(
          filters.startDate,
          filters.endDate
        );
        shouldUseAPI = true;
      } else if (Object.values(filters).some((val) => val !== "")) {
        // Use search API for complex filtering or use context with query params
        const searchFilters = Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== "")
        );

        // Try using context with query params first
        await getAppointments(searchFilters);
        return; // Exit early as context will update allAppointments
      } else {
        // Fetch all appointments using context
        await getAppointments();
        return; // Exit early as context will update allAppointments
      }

      if (shouldUseAPI) {
        // Handle direct API call results
        const appointmentsData = result.appointments || result.data || result;
        setFilteredAppointments(
          Array.isArray(appointmentsData) ? appointmentsData : []
        );
      }
    } catch (error) {
      console.error("Error applying filters:", error);
      // Fallback to client-side filtering
      clientSideFilter();
    }
  };

  // Client-side filtering as fallback
  const clientSideFilter = () => {
    if (!Array.isArray(allAppointments)) {
      setFilteredAppointments([]);
      return;
    }

    let filtered = [...allAppointments];

    // Apply quick filter
    if (filters.quickFilter) {
      const dateRange = getDateRangeForQuickFilter(filters.quickFilter);
      if (dateRange) {
        filtered = filtered.filter(
          (apt) =>
            apt.date >= dateRange.startDate && apt.date <= dateRange.endDate
        );
      }
    }

    if (filters.search) {
      filtered = filtered.filter(
        (apt) =>
          getPetName(apt.pets)
            ?.toLowerCase()
            .includes(filters.search.toLowerCase()) ||
          getOwnerName(apt.pets)
            ?.toLowerCase()
            .includes(filters.search.toLowerCase()) ||
          apt.type?.toLowerCase().includes(filters.search.toLowerCase()) ||
          getDoctorName(apt.doctor)
            ?.toLowerCase()
            .includes(filters.search.toLowerCase())
      );
    }

    if (filters.status) {
      filtered = filtered.filter((apt) => apt.status === filters.status);
    }

    if (filters.type) {
      filtered = filtered.filter(
        (apt) => apt.type === filters.type.toLowerCase()
      );
    }

    if (filters.doctor) {
      filtered = filtered.filter(
        (apt) => getDoctorName(apt.doctor) === filters.doctor
      );
    }

    if (filters.date) {
      filtered = filtered.filter((apt) => apt.date === filters.date);
    }

    if (filters.startDate && filters.endDate) {
      filtered = filtered.filter(
        (apt) => apt.date >= filters.startDate && apt.date <= filters.endDate
      );
    }

    setFilteredAppointments(filtered);
  };

  // Apply filters when filter values change
  useEffect(() => {
    if (allAppointments?.length >= 0) {
      // Use debounced filtering to avoid too many API calls
      const timeoutId = setTimeout(() => {
        if (Object.values(filters).some((val) => val !== "")) {
          applyFilters();
        } else {
          setFilteredAppointments(
            Array.isArray(allAppointments) ? allAppointments : []
          );
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [filters, allAppointments]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      type: "",
      doctor: "",
      date: "",
      startDate: "",
      endDate: "",
      quickFilter: "",
    });
    setFilteredAppointments(
      Array.isArray(allAppointments) ? allAppointments : []
    );
  };

  // Enhanced pagination handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Enhanced pagination navigation
  const goToFirstPage = () => handlePageChange(1);
  const goToLastPage = () => handlePageChange(totalPages);
  const goToPreviousPage = () => handlePageChange(currentPage - 1);
  const goToNextPage = () => handlePageChange(currentPage + 1);

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No date";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Invalid date";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "No time";
    try {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "Invalid time";
    }
  };

  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setShowModal(true);
  };

  const handleStatusUpdate = async (appointmentId, newStatus) => {
    try {
      setActionLoading(true);

      // Use specific API functions for status updates
      let result;
      switch (newStatus) {
        case "confirmed":
          result = await confirmAppointment(appointmentId);
          break;
        case "cancelled":
          result = await cancelAppointment(appointmentId);
          break;
        case "completed":
          result = await completeAppointment(appointmentId);
          break;
        default:
          result = await updateAppointment(appointmentId, {
            status: newStatus,
          });
      }

      // Refresh appointments from context after update
      await getAppointments();

      // Update selected appointment if it's being viewed
      if (selectedAppointment?._id === appointmentId) {
        setSelectedAppointment((prev) => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error("Error updating appointment status:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm("Are you sure you want to delete this appointment?")) {
      return;
    }

    try {
      setActionLoading(true);
      await deleteAppointment(appointmentId);

      // Refresh appointments from context after deletion
      await getAppointments();

      // Close modal if deleted appointment was being viewed
      if (selectedAppointment?._id === appointmentId) {
        setShowModal(false);
        setSelectedAppointment(null);
      }
    } catch (error) {
      console.error("Error deleting appointment:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const loadTodaysAppointments = async () => {
    try {
      setFilters((prev) => ({ ...prev, quickFilter: "today" }));
    } catch (error) {
      console.error("Error loading today's appointments:", error);
    }
  };

  // Enhanced pagination component with better UI
  const renderPagination = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(
      currentPage * itemsPerPage,
      filteredAppointments.length
    );

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">
              Showing {startIndex} to {endIndex} of{" "}
              {filteredAppointments.length} appointments
            </span>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Show:</span>
              <select
                value={itemsPerPage}
                onChange={(e) =>
                  handleItemsPerPageChange(Number(e.target.value))
                }
                className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {itemsPerPageOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-700">per page</span>
            </div>
          </div>

          <div className="flex items-center space-x-1">
            {/* First page button */}
            <button
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded"
              title="First page"
            >
              First
            </button>

            {/* Previous page button */}
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded"
              title="Previous page"
            >
              <ChevronLeft size={20} />
            </button>

            {/* Page numbers */}
            {startPage > 1 && (
              <>
                <button
                  onClick={() => handlePageChange(1)}
                  className="px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  1
                </button>
                {startPage > 2 && (
                  <span className="px-2 text-gray-400">...</span>
                )}
              </>
            )}

            {pageNumbers.map((number) => (
              <button
                key={number}
                onClick={() => handlePageChange(number)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  currentPage === number
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {number}
              </button>
            ))}

            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && (
                  <span className="px-2 text-gray-400">...</span>
                )}
                <button
                  onClick={() => handlePageChange(totalPages)}
                  className="px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  {totalPages}
                </button>
              </>
            )}

            {/* Next page button */}
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded"
              title="Next page"
            >
              <ChevronRight size={20} />
            </button>

            {/* Last page button */}
            <button
              onClick={goToLastPage}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded"
              title="Last page"
            >
              Last
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="py-6 max-w-7xl mx-auto">
      {/* Error Display */}
      {allAppointmentsError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="text-red-600" size={20} />
          <span className="text-red-700">{allAppointmentsError}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Appointment Management
          </h1>
          <p className="text-gray-600 mt-1">
            Managing {allAppointments?.length || 0} total appointments
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={loadTodaysAppointments}
            className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm sm:text-base order-2 sm:order-1"
          >
            <Calendar size={18} className="sm:w-5 sm:h-5" />
            <span className="whitespace-nowrap">Today's Appointments</span>
          </button>

          <button className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm sm:text-base order-1 sm:order-2">
            <Plus size={18} className="sm:w-5 sm:h-5" />
            <span>New Appointment</span>
          </button>
        </div>
      </div>

      {/* Quick Filters Bar */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700 mr-2">
            Quick Filters:
          </span>
          {quickFilters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => handleFilterChange("quickFilter", filter.value)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                filters.quickFilter === filter.value
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search by pet name, owner, type, or doctor..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter size={20} />
            Advanced Filters
          </button>

          {/* Clear Filters */}
          <button
            onClick={clearFilters}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            <X size={20} />
            Clear All
          </button>
        </div>

        {/* Extended Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {statuses.map((status) => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filters.type}
                  onChange={(e) => handleFilterChange("type", e.target.value)}
                >
                  <option value="">All Types</option>
                  {appointmentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Doctor Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Doctor
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filters.doctor}
                  onChange={(e) => handleFilterChange("doctor", e.target.value)}
                >
                  <option value="">All Doctors</option>
                  {doctors.map((doctor) => (
                    <option key={doctor} value={doctor}>
                      {doctor}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Specific Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filters.date}
                  onChange={(e) => handleFilterChange("date", e.target.value)}
                />
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mb-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <p className="text-gray-600">
            Showing {filteredAppointments?.length || 0} of{" "}
            {allAppointments?.length || 0} appointments
          </p>
          {filteredAppointments?.length > 0 && (
            <p className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </p>
          )}
        </div>
        <button
          onClick={() => getAppointments()}
          disabled={allAppointmentsLoading}
          className="text-blue-600 hover:text-blue-800 disabled:opacity-50 flex items-center gap-1"
        >
          <svg
            className={`w-4 h-4 ${
              allAppointmentsLoading ? "animate-spin" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh
        </button>

        {/* Temporary Debug Button */}
        <button
          onClick={() => {
            console.log("All Appointments:", allAppointments);
            if (allAppointments && allAppointments.length > 0) {
              console.log("First Appointment:", allAppointments[0]);
              console.log("Pet Object:", allAppointments[0]?.pets);
              console.log(
                "Owner Path 1:",
                allAppointments[0]?.pets?.ownerId?.userDetails
              );
              console.log("Pet Name:", getPetName(allAppointments[0]?.pets));
              console.log(
                "Owner Name:",
                getOwnerName(allAppointments[0]?.pets)
              );
            }
          }}
          className="text-green-600 hover:text-green-800 text-sm"
        >
          Debug Data
        </button>
      </div>

      {/* Appointments Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden relative">
        {allAppointmentsLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="text-gray-600">Loading appointments...</span>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pet & Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Doctor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Change Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(displayedAppointments) &&
                displayedAppointments.map((appointment) => (
                  <tr key={appointment._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-600 font-medium">
                              {getPetName(appointment.pets)?.charAt(0) || "P"}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {getPetName(appointment.pets)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {getOwnerName(appointment.pets)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {appointment.type
                          ? appointment.type.charAt(0).toUpperCase() +
                            appointment.type.slice(1)
                          : "No type"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar size={16} className="text-gray-400 mr-1" />
                        <span className="text-sm text-gray-900 mr-2">
                          {formatDate(appointment.date)}
                        </span>
                        <Clock size={16} className="text-gray-400 mr-1" />
                        <span className="text-sm text-gray-900">
                          {formatTime(appointment.time)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User size={16} className="text-gray-400 mr-1" />
                        <span className="text-sm text-gray-900">
                          {getDoctorName(appointment.doctor)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            appointment.status
                          )}`}
                        >
                          {appointment.status
                            ? appointment.status.charAt(0).toUpperCase() +
                              appointment.status.slice(1)
                            : "No status"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        onChange={(e) =>
                          handleStatusUpdate(appointment._id, e.target.value)
                        }
                        className="text-xs border border-gray-300 rounded px-2 py-1"
                        disabled={actionLoading}
                        defaultValue=""
                      >
                        <option value="" disabled>
                          Change Status
                        </option>
                        <option value="confirmed">Confirm</option>
                        <option value="pending">Pending</option>
                        <option value="cancelled">Cancel</option>
                        <option value="completed">Complete</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewDetails(appointment)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          className="text-green-600 hover:text-green-900"
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteAppointment(appointment._id)
                          }
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                          disabled={actionLoading}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {(!Array.isArray(displayedAppointments) ||
          displayedAppointments.length === 0) &&
          !allAppointmentsLoading && (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">No appointments found</p>
                <p className="text-sm">
                  Try adjusting your filters or search terms
                </p>
              </div>
            </div>
          )}
      </div>

      {/* Enhanced Pagination Component */}
      {filteredAppointments?.length > 0 && renderPagination()}

      {/* Appointment Details Modal */}
      {showModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  Appointment Details
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pet Name
                  </label>
                  <p className="text-gray-900">
                    {getPetName(selectedAppointment.pets)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Owner Name
                  </label>
                  <p className="text-gray-900">
                    {getOwnerName(selectedAppointment.pets)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <p className="text-gray-900">
                    {selectedAppointment.type
                      ? selectedAppointment.type.charAt(0).toUpperCase() +
                        selectedAppointment.type.slice(1)
                      : "No type"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <p className="text-gray-900">
                    {formatDate(selectedAppointment.date)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time
                  </label>
                  <p className="text-gray-900">
                    {formatTime(selectedAppointment.time)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Doctor
                  </label>
                  <p className="text-gray-900">
                    {getDoctorName(selectedAppointment.doctor)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                      selectedAppointment.status
                    )}`}
                  >
                    {selectedAppointment.status
                      ? selectedAppointment.status.charAt(0).toUpperCase() +
                        selectedAppointment.status.slice(1)
                      : "No status"}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created
                  </label>
                  <p className="text-gray-900">
                    {formatDate(selectedAppointment.createdAt)}
                  </p>
                </div>
              </div>

              {selectedAppointment.notes && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">
                    {selectedAppointment.notes}
                  </p>
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Edit Appointment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentManager;
